import { StatusChip } from "@/components/StatusChip";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Target, Building2, Users2, CheckSquare, FileText, Map, Package } from "lucide-react";

const PRIORITY = ["Hot", "Warm", "Cold"];
const LIFECYCLE = ["Prospect", "Lead", "Opportunity", "Advertiser", "Former Customer", "Archived"];
export const LEAD_STAGES = [
  "New Lead", "Contact Attempted", "Contacted / Conversation Started", "Qualified / Interested",
  "Meeting Scheduled", "Meeting Completed / Follow-Up", "Advertising Options / Proposal Discussed",
  "Decision Pending", "Won / Advertiser", "Lost / Not Moving Forward", "Nurture / Future Follow-Up",
];
const NEXT_ACTIONS = [
  "Call", "Email", "Text / Message", "Visit Business", "Schedule Meeting", "Attend Meeting",
  "Send Information", "Send Advertising Options", "Send Agreement", "Follow Up", "Await Decision", "Other",
];
const LOST_REASONS = [
  "Not Interested", "Price", "No Budget", "Timing", "Already Advertising Elsewhere",
  "Could Not Reach Decision Maker", "Business Closed", "No Response", "Chose Competitor", "Other",
];

export const RESOURCES = {
  leads: {
    title: "Leads", singular: "Lead", endpoint: "leads", icon: Target, duplicateCheck: "/leads/check-duplicate",
    columns: [
      { key: "business_name", label: "Business", bold: true },
      { key: "city", label: "City" },
      { key: "pipeline_stage", label: "Stage", chip: true },
      { key: "temperature", label: "Temp", chip: true },
      { key: "lead_source", label: "Source" },
      { key: "assigned_to_name", label: "Owner", muted: true },
      { key: "potential_value", label: "Est. Value", render: (v) => fmtMoney(v) },
    ],
    filters: [
      { name: "status", label: "Status", optionsSource: "lead_statuses" },
      { name: "priority", label: "Priority", options: PRIORITY },
      { name: "lead_source", label: "Source", optionsSource: "lead_sources" },
      { name: "unassigned", label: "Assignment", options: ["Unassigned"] },
    ],
    fields: [
      { name: "business_name", label: "Business Name", required: true },
      { name: "contact_name", label: "Main Contact" },
      { name: "website", label: "Website" },
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email" },
      { name: "address", label: "Address" },
      { name: "city", label: "City" },
      { name: "province", label: "Province/State" },
      { name: "zone_id", label: "Market › Zone", type: "select", optionsSource: "zone_options" },
      { name: "pipeline_stage", label: "Pipeline Stage", type: "select", options: LEAD_STAGES },
      { name: "temperature", label: "Lead Temperature", type: "select", options: ["Hot", "Warm", "Cold"] },
      { name: "lead_source", label: "Lead Source", type: "select", optionsSource: "lead_sources" },
      { name: "status", label: "Status", type: "select", optionsSource: "lead_statuses" },
      { name: "priority", label: "Priority", type: "select", options: PRIORITY },
      { name: "advertising_interest", label: "Advertising Interest", type: "tags" },
      { name: "potential_value", label: "Estimated Opportunity Value", type: "number" },
      { name: "assigned_to", label: "Assigned Salesperson", type: "select", optionsSource: "users" },
      { name: "next_action", label: "Next Action", type: "select", options: NEXT_ACTIONS },
      { name: "next_follow_up", label: "Next Follow-Up Date", type: "date" },
      { name: "next_followup_time", label: "Next Follow-Up Time", type: "time" },
      { name: "lost_reason", label: "Lost Reason", type: "select", options: LOST_REASONS },
      { name: "tags", label: "Tags (comma separated)", type: "tags", full: true },
      { name: "notes", label: "Notes", type: "textarea", full: true },
    ],
  },
  businesses: {
    title: "Businesses", singular: "Business", endpoint: "businesses", icon: Building2, rowLink: true,
    columns: [
      { key: "name", label: "Business", bold: true },
      { key: "city", label: "City" },
      { key: "zone", label: "Zone" },
      { key: "lifecycle", label: "Lifecycle", chip: true },
      { key: "category", label: "Category" },
      { key: "phone", label: "Phone" },
      { key: "assigned_to_name", label: "Owner", muted: true },
    ],
    filters: [{ name: "lifecycle", label: "Lifecycle", options: LIFECYCLE }],
    fields: [
      { name: "name", label: "Business Name", required: true },
      { name: "category", label: "Category" },
      { name: "website", label: "Website" },
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email" },
      { name: "city", label: "City" },
      { name: "province", label: "Province/State" },
      { name: "zone_id", label: "Market › Zone", type: "select", optionsSource: "zone_options" },
      { name: "lifecycle", label: "Lifecycle", type: "select", options: LIFECYCLE, required: true },
      { name: "assigned_to", label: "Assigned To", type: "select", optionsSource: "users" },
      { name: "tags", label: "Tags", type: "tags", full: true },
    ],
  },
  contacts: {
    title: "Contacts", singular: "Contact", endpoint: "contacts", icon: Users2,
    columns: [
      { key: "first_name", label: "First", bold: true },
      { key: "last_name", label: "Last" },
      { key: "title", label: "Title" },
      { key: "business_name", label: "Business" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
    ],
    fields: [
      { name: "first_name", label: "First Name", required: true },
      { name: "last_name", label: "Last Name" },
      { name: "title", label: "Title" },
      { name: "business_id", label: "Business", type: "select", optionsSource: "businesses" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Phone" },
      { name: "mobile", label: "Mobile" },
      { name: "preferred_method", label: "Preferred Method", type: "select", options: ["Phone", "Email", "Mobile", "In Person"] },
      { name: "do_not_contact", label: "Do Not Contact", type: "switch" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  tasks: {
    title: "Tasks & Follow-Ups", singular: "Task", endpoint: "tasks", icon: CheckSquare,
    columns: [
      { key: "title", label: "Task", bold: true },
      { key: "business_name", label: "Business" },
      { key: "due_date", label: "Due", render: (v) => fmtDate(v) },
      { key: "priority", label: "Priority", chip: true },
      { key: "status", label: "Status", chip: true },
      { key: "assigned_to_name", label: "Owner", muted: true },
    ],
    filters: [{ name: "status", label: "Status", options: ["Open", "Completed"] }],
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "business_id", label: "Business", type: "select", optionsSource: "businesses" },
      { name: "assigned_to", label: "Assigned To", type: "select", optionsSource: "users" },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"] },
      { name: "status", label: "Status", type: "select", options: ["Open", "Completed"] },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  contracts: {
    title: "Sales & Contracts", singular: "Contract", endpoint: "contracts", icon: FileText, createEndpoint: "contracts",
    pdf: (row) => ({ path: `/pdf/contract/${row.id}`, filename: `contract_${row.contract_number}.pdf` }),
    columns: [
      { key: "contract_number", label: "Contract #", bold: true },
      { key: "business_name", label: "Advertiser" },
      { key: "product_name", label: "Product" },
      { key: "term", label: "Term" },
      { key: "contract_total", label: "Total", render: (v) => fmtMoney(v) },
      { key: "payment_status", label: "Payment", chip: true },
      { key: "agreement_status", label: "Agreement", chip: true },
    ],
    fields: [
      { name: "business_id", label: "Business", type: "select", optionsSource: "businesses", required: true },
      { name: "business_name", label: "Advertiser Name" },
      { name: "product_name", label: "Product", type: "select", optionsSource: "products" },
      { name: "monthly_rate", label: "Monthly Rate", type: "number" },
      { name: "contract_total", label: "Contract Total", type: "number", required: true },
      { name: "discount", label: "Discount", type: "number" },
      { name: "term", label: "Term", type: "select", options: ["6 Months", "12 Months"], required: true },
      { name: "zone_id", label: "Market › Zone", type: "select", optionsSource: "zone_options" },
      { name: "assigned_to", label: "Salesperson", type: "select", optionsSource: "users" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date", type: "date" },
      { name: "payment_status", label: "Payment Status", type: "select", options: ["Unpaid", "Deposit Received", "Partially Paid", "Paid", "Overdue", "Cancelled"] },
      { name: "agreement_status", label: "Agreement Status", type: "select", options: ["Draft", "Signed", "Cancelled"] },
    ],
  },
  products: {
    title: "Products & Services", singular: "Product / Service", endpoint: "products", icon: Package,
    columns: [
      { key: "name", label: "Product / Service", bold: true },
      { key: "category", label: "Category" },
      { key: "base_price", label: "Base Price", render: (v, row) => fmtMoney(v ?? row.founding_price ?? row.regular_price) },
      { key: "billing_type", label: "Billing", render: (v, row) => v || row.billing },
      { key: "add_on", label: "Add-On", chip: true, render: (v) => (v ? "Add-On" : "") },
      { key: "active", label: "Active", chip: true, render: (v) => (v === false ? "Inactive" : "Active") },
    ],
    filters: [{ name: "category", label: "Category" }],
    fields: [
      { name: "name", label: "Product / Service Name", required: true },
      { name: "category", label: "Category" },
      { name: "description", label: "Short Description" },
      { name: "detailed_description", label: "Detailed Description", type: "textarea", full: true },
      { name: "customer_facing_description", label: "Customer-Facing Description", type: "textarea", full: true },
      { name: "base_price", label: "Base Price", type: "number" },
      { name: "billing_type", label: "Billing Type", type: "select", options: ["One-Time", "Monthly", "Per Zone", "Contract", "Custom"] },
      { name: "contract_term", label: "Contract Term", type: "select", options: ["None", "6 Months", "12 Months", "24 Months"] },
      { name: "zone_pricing", label: "Zone Pricing Notes" },
      { name: "founding_price", label: "Founding Price (legacy)", type: "number" },
      { name: "renewal_price", label: "Renewal Price (legacy)", type: "number" },
      { name: "add_on", label: "Add-On", type: "switch" },
      { name: "taxable", label: "Taxable", type: "switch" },
      { name: "active", label: "Active", type: "switch" },
      { name: "internal_notes", label: "Internal Notes", type: "textarea", full: true },
    ],
  },
  territories: {
    title: "Territories", singular: "Territory", endpoint: "territories", icon: Map,
    columns: [
      { key: "name", label: "Territory", bold: true },
      { key: "owner_name", label: "Primary Owner", muted: true },
    ],
    fields: [
      { name: "name", label: "Territory Name", required: true },
      { name: "owner_id", label: "Primary Owner (auto-assigns website leads for this territory)", type: "select", optionsSource: "users", full: true },
    ],
  },
  zones: {
    title: "Zones", singular: "Zone", endpoint: "zones", icon: Map,
    columns: [
      { key: "name", label: "Zone", bold: true },
      { key: "territory", label: "Territory" },
      { key: "owner_name", label: "Zone Owner", muted: true },
    ],
    fields: [
      { name: "name", label: "Zone Name", required: true },
      { name: "territory", label: "Territory", type: "select", optionsSource: "territories" },
      { name: "owner_id", label: "Zone Owner (auto-assigns website leads for this zone)", type: "select", optionsSource: "users", full: true },
    ],
  },
};
